import React, { useRef, useState, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  InputNumber,
  Switch,
  Spin,
  Empty,
  App,
  Tooltip,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  LinkOutlined,
  DisconnectOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import {
  useGetProductsWithStockQuery,
  useUpdateProductAvailabilityMutation,
  useLinkProductToInventoryMutation,
  useUnlinkProductFromInventoryMutation,
} from '../../services/productsApi';
import {
  useGetInventoryCategoriesQuery,
  useCreateInventoryCategoryMutation,
  useAdjustStockMutation,
} from '../../services/inventoryApi';
import { ProductStock, LinkProductInventoryRequest } from '../../types/products';
import { AdjustStockRequest } from '../../types/inventory';
import { formatStock } from '../../utils/unitHelpers';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const UNIT_OPTIONS = [
  { label: 'Kilogramos (kg)', value: 'kg' },
  { label: 'Litros (L)', value: 'L' },
  { label: 'Piezas (pz)', value: 'pz' },
  { label: 'Cajas', value: 'caja' },
  { label: 'Gramos (g)', value: 'g' },
  { label: 'Mililitros (ml)', value: 'ml' },
];

const normalizeText = (text: string) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const ProductStockTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { message: messageApi, modal } = App.useApp();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [linkTypeFilter, setLinkTypeFilter] = useState<string | undefined>(undefined);
  const [availabilityFilter, setAvailabilityFilter] = useState<string | undefined>(undefined);

  // Stock adjust modal
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [adjustingProduct, setAdjustingProduct] = useState<ProductStock | null>(null);
  const [stockForm] = Form.useForm();

  // Link inventory modal
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkingProduct, setLinkingProduct] = useState<ProductStock | null>(null);
  const [linkForm] = Form.useForm();

  // API hooks
  const {
    data: products = [],
    isLoading,
  } = useGetProductsWithStockQuery();
  const { data: inventoryCategories = [] } = useGetInventoryCategoriesQuery();
  const [updateAvailability] = useUpdateProductAvailabilityMutation();
  const [adjustStock, { isLoading: adjustingStock }] = useAdjustStockMutation();
  const [linkProduct, { isLoading: linkingLoading }] = useLinkProductToInventoryMutation();
  const [unlinkProduct] = useUnlinkProductFromInventoryMutation();
  const [createCategory, { isLoading: creatingCategory }] = useCreateInventoryCategoryMutation();

  // Inline category creation
  const [newCategoryName, setNewCategoryName] = useState('');
  const categoryInputRef = useRef<any>(null);

  // Filtered products
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch =
        !searchTerm ||
        normalizeText(p.productName).includes(normalizeText(searchTerm)) ||
        normalizeText(p.categoryName).includes(normalizeText(searchTerm));

      const matchesLinkType =
        !linkTypeFilter || p.inventoryLinkType === linkTypeFilter;

      const matchesAvailability =
        availabilityFilter === undefined ||
        availabilityFilter === '' ||
        (availabilityFilter === 'available' && p.isAvailable) ||
        (availabilityFilter === 'unavailable' && !p.isAvailable);

      return matchesSearch && matchesLinkType && matchesAvailability;
    });
  }, [products, searchTerm, linkTypeFilter, availabilityFilter]);

  // =========================================================
  // Availability toggle
  // =========================================================

  const handleToggleAvailability = async (product: ProductStock) => {
    try {
      await updateAvailability({
        id: product.productId,
        isAvailable: !product.isAvailable,
      }).unwrap();
      messageApi.success(
        product.isAvailable ? 'Producto marcado como agotado' : 'Producto marcado como disponible'
      );
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al cambiar disponibilidad');
    }
  };

  // =========================================================
  // Stock adjustment
  // =========================================================

  const handleOpenStockModal = (product: ProductStock) => {
    setAdjustingProduct(product);
    stockForm.resetFields();
    stockForm.setFieldsValue({ quantity: 0, notes: '' });
    setStockModalOpen(true);
  };

  const handleCloseStockModal = () => {
    setStockModalOpen(false);
    setAdjustingProduct(null);
    stockForm.resetFields();
  };

  const handleAdjustStock = async () => {
    if (!adjustingProduct?.inventoryItemId) return;
    try {
      const values = await stockForm.validateFields();
      const data: AdjustStockRequest = {
        quantity: values.quantity,
        notes: values.notes || undefined,
      };
      await adjustStock({ id: adjustingProduct.inventoryItemId, data }).unwrap();
      messageApi.success('Stock ajustado exitosamente');
      handleCloseStockModal();
    } catch (error: any) {
      if (error?.errorFields) return;
      messageApi.error(error?.data?.message || 'Error al ajustar el stock');
    }
  };

  // =========================================================
  // Inline category creation
  // =========================================================

  const handleCreateCategory = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      const result = await createCategory({
        name: newCategoryName.trim(),
        isActive: true,
      }).unwrap();
      setNewCategoryName('');
      linkForm.setFieldsValue({ inventoryCategoryId: result.id });
      messageApi.success('Categoria creada');
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al crear categoria');
    }
  };

  // =========================================================
  // Link to inventory
  // =========================================================

  const handleOpenLinkModal = (product: ProductStock) => {
    setLinkingProduct(product);
    linkForm.resetFields();
    linkForm.setFieldsValue({
      currentStock: 0,
      minimumStock: 0,
      costPerUnit: 0,
      conversionFactor: 1,
    });
    setLinkModalOpen(true);
  };

  const handleCloseLinkModal = () => {
    setLinkModalOpen(false);
    setLinkingProduct(null);
    linkForm.resetFields();
  };

  const handleLinkProduct = async () => {
    if (!linkingProduct) return;
    try {
      const values = await linkForm.validateFields();
      const data: LinkProductInventoryRequest = {
        inventoryCategoryId: values.inventoryCategoryId,
        unit: values.unit,
        currentStock: values.currentStock,
        minimumStock: values.minimumStock,
        costPerUnit: values.costPerUnit,
        conversionFactor: values.conversionFactor,
      };
      await linkProduct({ id: linkingProduct.productId, data }).unwrap();
      messageApi.success('Producto vinculado al inventario exitosamente');
      handleCloseLinkModal();
    } catch (error: any) {
      if (error?.errorFields) return;
      messageApi.error(error?.data?.message || 'Error al vincular el producto');
    }
  };

  // =========================================================
  // Unlink from inventory
  // =========================================================

  const handleUnlinkProduct = (product: ProductStock) => {
    modal.confirm({
      title: 'Desvincular del inventario',
      content: `¿Desvincular "${product.productName}" del inventario? El insumo seguira existiendo en inventario pero ya no se descontara automaticamente al vender este producto.`,
      okText: 'Desvincular',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await unlinkProduct(product.productId).unwrap();
          messageApi.success('Producto desvinculado del inventario');
        } catch (error: any) {
          messageApi.error(error?.data?.message || 'Error al desvincular');
        }
      },
    });
  };

  // =========================================================
  // Link type tag
  // =========================================================

  const renderLinkTag = (type: string) => {
    switch (type) {
      case 'direct':
        return <Tag color="blue">Insumo directo</Tag>;
      case 'recipe':
        return <Tag color="purple">Receta</Tag>;
      default:
        return <Tag>Sin inventario</Tag>;
    }
  };

  // =========================================================
  // Stock display
  // =========================================================

  const renderStockInfo = (product: ProductStock) => {
    if (product.inventoryLinkType === 'direct') {
      const stockColor = product.isLowStock ? 'red' : 'green';
      return (
        <Tag color={stockColor}>
          {formatStock(product.currentStock ?? 0)} {product.unit}
        </Tag>
      );
    }
    if (product.inventoryLinkType === 'recipe') {
      return (
        <Tooltip title={product.hasSufficientStock ? 'Stock suficiente' : 'Stock insuficiente'}>
          <Tag color={product.hasSufficientStock ? 'green' : 'orange'}>
            {product.ingredientCount} ingredientes
          </Tag>
        </Tooltip>
      );
    }
    return <Text type="secondary">-</Text>;
  };

  // =========================================================
  // Table columns
  // =========================================================

  const columns: ColumnsType<ProductStock> = [
    {
      title: 'Producto',
      dataIndex: 'productName',
      key: 'productName',
      sorter: (a, b) => a.productName.localeCompare(b.productName),
      render: (name: string, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.categoryName} &middot; {currencySymbol}{record.price.toFixed(2)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Vinculo',
      key: 'linkType',
      width: 140,
      responsive: ['md'] as any,
      render: (_: any, record: ProductStock) => renderLinkTag(record.inventoryLinkType),
    },
    {
      title: 'Stock',
      key: 'stock',
      width: 150,
      render: (_: any, record: ProductStock) => renderStockInfo(record),
    },
    {
      title: 'Disponible',
      key: 'available',
      width: 100,
      render: (_: any, record: ProductStock) => (
        <Switch
          checked={record.isAvailable}
          size="small"
          onChange={() => handleToggleAvailability(record)}
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 160,
      render: (_: any, record: ProductStock) => {
        if (record.inventoryLinkType === 'direct') {
          return (
            <Space size="small">
              <Tooltip title="Ajustar stock">
                <Button
                  size="small"
                  icon={<PlusCircleOutlined />}
                  onClick={() => handleOpenStockModal(record)}
                />
              </Tooltip>
              <Tooltip title="Desvincular">
                <Button
                  size="small"
                  danger
                  icon={<DisconnectOutlined />}
                  onClick={() => handleUnlinkProduct(record)}
                />
              </Tooltip>
            </Space>
          );
        }
        if (record.inventoryLinkType === 'none') {
          return (
            <Tooltip title="Vincular como insumo">
              <Button
                size="small"
                type="primary"
                ghost
                icon={<LinkOutlined />}
                onClick={() => handleOpenLinkModal(record)}
              >
                {!isMobile && 'Vincular'}
              </Button>
            </Tooltip>
          );
        }
        // Recipe type - managed from Inventory > Recipes
        return <Text type="secondary" style={{ fontSize: 12 }}>Ver en Recetas</Text>;
      },
    },
  ];

  // =========================================================
  // Mobile card
  // =========================================================

  const ProductCard: React.FC<{ product: ProductStock }> = ({ product }) => (
    <Card size="small" style={{ marginBottom: 8 }} hoverable>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Text strong>{product.productName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {product.categoryName} &middot; {currencySymbol}{product.price.toFixed(2)}
          </Text>
          <div style={{ marginTop: 4 }}>
            {renderLinkTag(product.inventoryLinkType)}
            {renderStockInfo(product)}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
          <Switch
            checked={product.isAvailable}
            size="small"
            checkedChildren="Disp."
            unCheckedChildren="Agot."
            onChange={() => handleToggleAvailability(product)}
          />
          {product.inventoryLinkType === 'direct' && (
            <Space size="small" style={{ marginTop: 4 }}>
              <Button size="small" icon={<PlusCircleOutlined />} onClick={() => handleOpenStockModal(product)} />
              <Button size="small" danger icon={<DisconnectOutlined />} onClick={() => handleUnlinkProduct(product)} />
            </Space>
          )}
          {product.inventoryLinkType === 'none' && (
            <Button
              size="small"
              type="primary"
              ghost
              icon={<LinkOutlined />}
              onClick={() => handleOpenLinkModal(product)}
              style={{ marginTop: 4 }}
            >
              Vincular
            </Button>
          )}
        </div>
      </div>
    </Card>
  );

  // =========================================================
  // Render
  // =========================================================

  return (
    <Card>
      {/* Filters */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Buscar productos..."
          prefix={<SearchOutlined />}
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 250, flex: 1 }}
        />
        <Select
          placeholder="Tipo de vinculo"
          allowClear
          value={linkTypeFilter}
          onChange={(v) => setLinkTypeFilter(v)}
          style={{ width: isMobile ? 140 : 170 }}
        >
          <Option value="direct">Insumo directo</Option>
          <Option value="recipe">Receta</Option>
          <Option value="none">Sin inventario</Option>
        </Select>
        <Select
          placeholder="Disponibilidad"
          allowClear
          value={availabilityFilter}
          onChange={(v) => setAvailabilityFilter(v)}
          style={{ width: isMobile ? 130 : 150 }}
        >
          <Option value="available">Disponible</Option>
          <Option value="unavailable">Agotado</Option>
        </Select>
      </div>

      {/* Table or Cards */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <Empty description="No se encontraron productos" />
      ) : isMobile ? (
        <div>
          {filteredProducts.map((p) => (
            <ProductCard key={p.productId} product={p} />
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey="productId"
          scroll={{ x: 700 }}
          pagination={{ pageSize: 15 }}
          size="middle"
        />
      )}

      {/* Stock Adjustment Modal */}
      <Modal
        title={`Ajustar Stock - ${adjustingProduct?.productName || ''}`}
        open={stockModalOpen}
        onCancel={handleCloseStockModal}
        onOk={handleAdjustStock}
        confirmLoading={adjustingStock}
        okText="Ajustar"
        cancelText="Cancelar"
        width={isMobile ? '98%' : 450}
        destroyOnClose
      >
        {adjustingProduct && (
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: 'var(--bg-secondary)', borderRadius: 8 }}>
            <Text type="secondary">Stock actual: </Text>
            <Tag color={adjustingProduct.isLowStock ? 'red' : 'green'}>
              {formatStock(adjustingProduct.currentStock ?? 0)} {adjustingProduct.unit}
            </Tag>
            {adjustingProduct.conversionFactor !== 1 && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Factor de conversion: {adjustingProduct.conversionFactor} {adjustingProduct.unit} por producto vendido
                </Text>
              </div>
            )}
          </div>
        )}
        <Form form={stockForm} layout="vertical" initialValues={{ quantity: 0, notes: '' }}>
          <Form.Item
            name="quantity"
            label="Cantidad (positiva para agregar, negativa para restar)"
            rules={[
              { required: true, message: 'La cantidad es requerida' },
              {
                validator: (_, value) => {
                  if (value === 0) return Promise.reject('La cantidad no puede ser cero');
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} precision={2} placeholder="Ej: 10 o -5" />
          </Form.Item>
          <Form.Item name="notes" label="Notas">
            <TextArea rows={3} placeholder="Motivo del ajuste (opcional)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Link Product to Inventory Modal */}
      <Modal
        title={`Vincular "${linkingProduct?.productName || ''}" al Inventario`}
        open={linkModalOpen}
        onCancel={handleCloseLinkModal}
        onOk={handleLinkProduct}
        confirmLoading={linkingLoading}
        okText="Vincular"
        cancelText="Cancelar"
        width={isMobile ? '98%' : 550}
        destroyOnClose
      >
        <Form form={linkForm} layout="vertical">
          <Form.Item
            name="inventoryCategoryId"
            label="Categoria de inventario"
            rules={[{ required: true, message: 'Seleccione una categoria' }]}
          >
            <Select
              placeholder="Seleccione una categoria"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Input
                      placeholder="Nueva categoria"
                      ref={categoryInputRef}
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      size="small"
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={handleCreateCategory}
                      loading={creatingCategory}
                      size="small"
                    >
                      Crear
                    </Button>
                  </Space>
                </>
              )}
            >
              {inventoryCategories
                .filter((c) => c.isActive)
                .map((c) => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="unit"
            label="Unidad de medida"
            rules={[{ required: true, message: 'Seleccione la unidad' }]}
          >
            <Select placeholder="Seleccione la unidad">
              {UNIT_OPTIONS.map((u) => (
                <Option key={u.value} value={u.value}>{u.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="currentStock"
              label="Stock inicial"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="minimumStock"
              label="Stock minimo"
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item
              name="costPerUnit"
              label={`Costo por unidad (${currencySymbol})`}
              style={{ flex: 1 }}
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="conversionFactor"
              label="Factor de conversion"
              style={{ flex: 1 }}
              tooltip="Cuantas unidades de inventario se descuentan por cada producto vendido. Ej: 1 cerveza = 1 pz, 1 cerveza de caja de 24 = 0.0417"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <InputNumber min={0.0001} precision={4} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProductStockTab;
